/**
 * RequestDemo.tsx — Public demo request form.
 * Rebuilt in Pass 7.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const C = {
  bgBase: '#080C18',
  bgPanel: '#0B0F1F',
  bgCard: '#0F1628',
  borderSubtle: 'rgba(148, 163, 184, 0.08)',
  borderDefault: 'rgba(148, 163, 184, 0.12)',
  textPrimary: '#E8EFF8',
  textSecondary: '#94A3B8',
  textTertiary: '#64748B',
  cyan: '#00D4FF',
  cyanLight: '#7DE5F5',
  cyanSoft: 'rgba(0, 212, 255, 0.10)',
  cyanGlow: 'rgba(0, 212, 255, 0.35)',
  success: '#10B981',
  danger: '#FF4560',
};

const F = { ui: "'DM Sans', -apple-system, system-ui, sans-serif" };

export default function RequestDemo() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', role: '', company: '', country: '', email: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = 'Request a demo | Meridian Registry';
    if (!document.querySelector('link[href*="DM+Sans"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const update = (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    try {
      await axios.post('/api/demo/request', form);
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell navigate={navigate} label="Request a demo">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '60px 32px 80px' }}>
        <h1 style={{
          fontSize: 36, fontWeight: 600,
          letterSpacing: '-0.03em',
          color: C.textPrimary,
          marginBottom: 12,
          margin: '0 0 12px 0',
        }}>Request a demo</h1>
        <div style={{
          fontSize: 12, fontWeight: 600,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: C.cyan, marginBottom: 20,
        }}>30 minutes · your data · your questions</div>
        <p style={{
          fontSize: 17, color: C.textSecondary,
          lineHeight: 1.6, marginBottom: 48,
          margin: '0 0 48px 0',
        }}>Tell us about your business and we'll be in touch to schedule a walkthrough. No sales pressure — just your outlet data matched against the registry, live.</p>

        {submitted ? (
          <div style={{
            background: 'rgba(16, 185, 129, 0.06)',
            border: `1px solid rgba(16, 185, 129, 0.3)`,
            borderRadius: 12, padding: '40px 32px',
            textAlign: 'center',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(16, 185, 129, 0.15)',
              border: `2px solid ${C.success}`,
              margin: '0 auto 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.success,
            }}>
              <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h2 style={{
              fontSize: 22, fontWeight: 600,
              color: C.textPrimary, marginBottom: 12,
              margin: '0 0 12px 0',
            }}>Thank you — we'll be in touch shortly.</h2>
            <p style={{
              color: C.textSecondary, fontSize: 14,
              marginBottom: 24, margin: '0 0 24px 0',
            }}>A member of the Meridian team will reach out within one business day.</p>
            <PrimaryBtn onClick={() => navigate('/')}>Back to home</PrimaryBtn>
          </div>
        ) : (
          <>
            {error && (
              <div style={{
                background: 'rgba(255, 69, 96, 0.08)',
                border: `1px solid rgba(255, 69, 96, 0.3)`,
                borderRadius: 8, padding: '12px 16px',
                marginBottom: 24,
                color: C.danger, fontSize: 14,
              }}>
                Something went wrong. Please email us directly at{' '}
                <a href="mailto:admin@meridianregistry.com" style={{ color: C.cyanLight, textDecoration: 'none' }}>
                  admin@meridianregistry.com
                </a>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <Field label="Name" required>
                <Input type="text" required placeholder="Your full name" value={form.name} onChange={update('name')} />
              </Field>
              <Field label="Role / Title" required>
                <Input type="text" required placeholder="e.g. Commercial Director, Data Analyst" value={form.role} onChange={update('role')} />
              </Field>
              <Field label="Company name" required>
                <Input type="text" required placeholder="Your company name" value={form.company} onChange={update('company')} />
              </Field>
              <Field label="Country / Market" required>
                <Input type="text" required placeholder="e.g. USA, UK, Germany" value={form.country} onChange={update('country')} />
              </Field>
              <Field label="Email address" required>
                <Input type="email" required placeholder="you@company.com" value={form.email} onChange={update('email')} />
              </Field>
              <Field label="What problem are you trying to solve?" required>
                <Textarea required rows={4}
                  placeholder="Tell us about your current outlet data challenges, what you're trying to measure, or what gaps you're trying to fill."
                  value={form.message} onChange={update('message')} />
              </Field>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                <PrimaryBtn type="submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send request'}
                </PrimaryBtn>
                <span style={{ fontSize: 13, color: C.textTertiary, alignSelf: 'center' }}>
                  Or email{' '}
                  <a href="mailto:admin@meridianregistry.com" style={{ color: C.cyanLight, textDecoration: 'none' }}>
                    admin@meridianregistry.com
                  </a>
                </span>
              </div>
            </form>
          </>
        )}
      </div>
    </PageShell>
  );
}

/* ═══════════════ Shared ═══════════════ */

function PageShell({
  navigate, label, children,
}: {
  navigate: (path: string) => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      background: C.bgBase,
      color: C.textPrimary,
      fontFamily: F.ui,
      minHeight: '100vh',
      position: 'relative',
      WebkitFontSmoothing: 'antialiased',
    }}>
      {/* Ambient glow */}
      <div aria-hidden style={{
        position: 'fixed',
        top: -300, right: -400,
        width: 900, height: 900,
        background: 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 65%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(8, 12, 24, 0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${C.borderSubtle}`,
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          padding: '14px 32px',
          display: 'flex', alignItems: 'center',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, padding: 0,
            }}>
            <img alt="Meridian" src="/Meridian_M_logo-transparent.png" style={{ height: 28 }} />
            <span style={{
              fontSize: 14, fontWeight: 700,
              letterSpacing: '0.22em',
              color: C.textPrimary, textTransform: 'uppercase',
            }}>Meridian</span>
          </button>
          <span style={{
            marginLeft: 16, paddingLeft: 16,
            borderLeft: `1px solid ${C.borderDefault}`,
            fontSize: 13, color: C.textSecondary,
          }}>{label}</span>
        </div>
      </nav>

      <main style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </main>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 11, fontWeight: 600,
        color: C.textTertiary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 6,
      }}>
        {label}
        {required && <span style={{ color: C.cyan, marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      onFocus={e => {
        e.currentTarget.style.borderColor = C.cyan;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${C.cyanSoft}`;
        props.onFocus?.(e);
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = C.borderDefault;
        e.currentTarget.style.boxShadow = 'none';
        props.onBlur?.(e);
      }}
      style={{
        width: '100%',
        background: C.bgCard,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 8,
        padding: '11px 14px',
        color: C.textPrimary,
        fontFamily: F.ui,
        fontSize: 14,
        outline: 'none',
        transition: 'all 150ms ease',
        boxSizing: 'border-box',
        ...props.style,
      }}
    />
  );
}

function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      onFocus={e => {
        e.currentTarget.style.borderColor = C.cyan;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${C.cyanSoft}`;
        props.onFocus?.(e);
      }}
      onBlur={e => {
        e.currentTarget.style.borderColor = C.borderDefault;
        e.currentTarget.style.boxShadow = 'none';
        props.onBlur?.(e);
      }}
      style={{
        width: '100%',
        background: C.bgCard,
        border: `1px solid ${C.borderDefault}`,
        borderRadius: 8,
        padding: '11px 14px',
        color: C.textPrimary,
        fontFamily: F.ui,
        fontSize: 14,
        outline: 'none',
        lineHeight: 1.6,
        resize: 'vertical',
        transition: 'all 150ms ease',
        boxSizing: 'border-box',
      }}
    />
  );
}

function PrimaryBtn({
  children, onClick, disabled, type = 'button',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.background = C.cyanLight;
        e.currentTarget.style.borderColor = C.cyanLight;
      }}
      onMouseLeave={e => {
        if (disabled) return;
        e.currentTarget.style.background = C.cyan;
        e.currentTarget.style.borderColor = C.cyan;
      }}
      style={{
        fontFamily: F.ui,
        fontWeight: 600, fontSize: 15,
        padding: '13px 28px',
        borderRadius: 10,
        background: C.cyan,
        color: '#000',
        border: `1px solid ${C.cyan}`,
        boxShadow: `0 0 0 1px ${C.cyanGlow}, 0 8px 24px -8px ${C.cyanGlow}`,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 120ms ease',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      {children}
    </button>
  );
}
